// evaluation.js - Evaluation flow controller.

import { APP } from './app.js';
import { AUTH } from './auth.js';
import { CALIBRATION } from './calibration.js';
import { CONFIG } from './config.js';
import { DB } from './db.js';
import { DICTATION } from './dictation.js';
import { DRAFT } from './draft.js';
import { getDomains, getEvalFormat, getSkillsFor, getTotalCriteria } from './evaluation-data.js';
import { pillarBandForTeam } from './young-age-data.js';
import { UI } from './ui.js';

export const EVALUATION = (() => {

  // ── STATE ────────────────────────────────────────────────────────────────────

  let _state = {
    player:        null,    // { id, name, is_goalkeeper }
    team:          null,    // { id, age_group }
    playerList:    [],      // all players in the current team
    playerIndex:   0,       // index of current player in list
    evalId:        null,    // set when editing an existing evaluation
    format:        'field_v1', // 'field_v1' (default) or 'gk_v1' - drives the
                            // skill list, descriptors and progress total
    domain:        null,    // currently active domain tab
    ratings:       {},      // { 'Domain::Skill': level }
    comments:      {},      // { 'Domain::Skill': comment }
    strongPoints:  '',
    nominatedStrength: '',   // the coach's pick of best skill
    nominatedWorking:  '',   // and the one needing most work
    workingPoints: '',
    sessionNotes:  '',
    ceilings:      [],      // team_score_ceilings rows for this team+season
    saving:        false,

    // Reference overlay (director-only): show a chosen coach's ratings as a
    // gold dot on each skill while the director makes their own evaluation.
    referenceCoach:   null,   // { coach_name, team_code }
    referenceOn:      false,  // is the overlay currently shown
    referenceRatings: {},     // { 'Domain::Skill': level } for the current player
    teamEvals:        null,   // cached DB.evaluations.getByTeam - for the picker
  };

  // ── INITIALISE ───────────────────────────────────────────────────────────────

  // Set up the evaluation screen for a new player.
  async function startNew(player, team, playerList, index) {
    const teamChanged = _state.team?.id !== team?.id;
    _state.player      = player;
    _state.team        = team;
    _state.playerList  = playerList;
    _state.playerIndex = index;
    // New eval - pick the rubric format from the player's GK flag.
    _state.format      = getEvalFormat(player);
    _state.evalId      = null;
    _state.evalId      = null;
    if (teamChanged) _resetReference();

    _resetRatings();
    await _loadCeilings();
    await _loadReferenceRatings();

    // Check for a saved draft
    const draft = DRAFT.load(player.id, team.id, AUTH.getCode());
    if (draft) {
      _state.ratings      = draft.ratings      || {};
      _state.comments     = draft.comments     || {};
      _state.strongPoints = draft.strongPoints || '';
      _state.nominatedStrength = draft.nominatedStrength || '';
      _state.nominatedWorking  = draft.nominatedWorking  || '';
      _state.workingPoints= draft.workingPoints|| '';
      _state.sessionNotes = draft.sessionNotes || '';
      _showDraftBanner(true);
    } else {
      _showDraftBanner(false);
    }

    // Set starting domain - feedback-only codes start on Feedback tab
    const pillars = AUTH.getPillars();
    const allDomains = getDomains();
    const visibleDomains = allDomains.filter(d => AUTH.canSeePillar(d));

    if (AUTH.isFeedback()) {
      _state.domain = 'Feedback';
    } else {
      _state.domain = visibleDomains[0] || 'Feedback';
    }

    _render();
  }

  // Load an existing evaluation into the form for editing.
  async function startEdit(player, team, playerList, index, evalId, evalData) {
    const teamChanged = _state.team?.id !== team?.id;
    _state.player      = player;
    _state.team        = team;
    _state.playerList  = playerList;
    _state.playerIndex = index;
    _state.evalId      = evalId;
    // Edit - preserve the format the eval was originally recorded against.
    // Old rows default to 'field_v1' (the migration set NOT NULL DEFAULT).
    _state.format      = evalData?.evaluation?.eval_format || 'field_v1';
    if (teamChanged) _resetReference();

    _resetRatings();
    await _loadCeilings();
    await _loadReferenceRatings();

    // Load existing ratings
    (evalData.ratings || []).forEach(r => {
      const key = `${r.domain}::${r.skill}`;
      _state.ratings[key]  = r.level;
      if (r.comment) _state.comments[key] = r.comment;
    });

    const ev = evalData.evaluation || {};
    _state.strongPoints  = ev.strong_points  || '';
    _state.nominatedStrength = ev.nominated_strength || '';
    _state.nominatedWorking  = ev.nominated_working  || '';
    _state.workingPoints = ev.working_points || '';
    _state.sessionNotes  = ev.notes          || '';

    _showDraftBanner(false);
    _state.domain = getDomains()[0] || 'Technical';
    _render();
  }

  // ── DOMAIN NAVIGATION ────────────────────────────────────────────────────────

  // Switch to a different domain tab.
  function setDomain(domain) {
    // Save any textarea content from feedback tab before switching
    if (_state.domain === 'Feedback') _syncFeedbackInputs();
    _state.domain = domain;
    _renderSkillsArea();
    _renderDomainTabs();
    document.getElementById('skills-area')?.scrollTo(0, 0);
  }

  // ── RATING ────────────────────────────────────────────────────────────────────

  function setRating(domain, skill, level) {
    // Client-side cap check. The DB trigger enforces the same rule, but a
    // toast is friendlier than a generic error.
    const cap = _ceilingFor(domain, skill);
    if (level > cap && !AUTH.isDirector()) {
      UI.toast(`Director has set the max for ${skill} on this team to ${cap}.`, 'error');
      return;
    }
    const key = `${domain}::${skill}`;
    // Toggle off if same level tapped again
    _state.ratings[key] = (_state.ratings[key] === level) ? 0 : level;
    _autoSaveDraft();
    _renderSkillsArea();
    _renderDomainTabs();
    _updateProgress();
  }

  // Set a comment for a skill.
  function setComment(domain, skill, comment) {
    const key = `${domain}::${skill}`;
    _state.comments[key] = comment;
    _autoSaveDraft();
  }

  // ── FEEDBACK ─────────────────────────────────────────────────────────────────

  function setStrongPoints(text)  { _state.strongPoints  = text; _autoSaveDraft(); }
  function setNominatedStrength(skill) { _state.nominatedStrength = skill || ''; _autoSaveDraft(); }
  function setNominatedWorking(skill)  { _state.nominatedWorking  = skill || ''; _autoSaveDraft(); }
  function setWorkingPoints(text) { _state.workingPoints = text; _autoSaveDraft(); }
  function setSessionNotes(text)  { _state.sessionNotes  = text; _autoSaveDraft(); }

  // ── PLAYER NAVIGATION ─────────────────────────────────────────────────────────

  // Navigate to the previous player.
  function prevPlayer() {
    if (_state.playerIndex <= 0) return;
    if (_hasUnsavedData()) {
      UI.confirmNavAway(() => _goToPlayer(_state.playerIndex - 1));
    } else {
      _goToPlayer(_state.playerIndex - 1);
    }
  }

  // Navigate to the next player.
  function nextPlayer() {
    if (_state.playerIndex >= _state.playerList.length - 1) return;
    if (_hasUnsavedData()) {
      UI.confirmNavAway(() => _goToPlayer(_state.playerIndex + 1));
    } else {
      _goToPlayer(_state.playerIndex + 1);
    }
  }

  // Attempt to navigate back to player list.
  function goBack() {
    if (_hasUnsavedData()) {
      UI.confirmNavAway(() => APP.showPlayerList());
    } else {
      APP.showPlayerList();
    }
  }

  // ── SUBMISSION ───────────────────────────────────────────────────────────────

  // Save the current evaluation to the database.
  async function submit() {
    if (_state.saving) return;

    // Sync feedback textareas before reading state
    _syncFeedbackInputs();

    const ratedKeys = Object.keys(_state.ratings).filter(k => _state.ratings[k] > 0);
    const hasFeedback = _state.strongPoints || _state.workingPoints || _state.sessionNotes;

    if (!ratedKeys.length && !hasFeedback) {
      UI.toast('Please add at least one rating or some feedback', 'error');
      return;
    }

    _state.saving = true;
    _setSaveButtonState('saving');

    // A coach evaluating with their PERSONAL staff code: attribute the row to
    // their canonical staff identity. team_code becomes a per-coach sentinel so
    // existing reports (which group by team_code) still separate coaches; the
    // real ownership is author_staff_id, enforced by RLS.
    // The DIRECTOR has no login code, so AUTH.getCode() is '' - without the
    // explicit sentinel the eval loses its 1.5x weight and director flag
    // (three blank May 2026 evals, deep-dive finding 2026-07-02).
    const staffCoach = AUTH.isStaff();
    const evalPayload = {
      player_id:     _state.player.id,
      team_id:       _state.team.id,
      season_id:     CONFIG.season.current,
      age_group:     _state.team.age_group,
      coach_name:    staffCoach ? AUTH.getStaffName() : AUTH.getCoachName(),
      team_code:     staffCoach ? `staff:${AUTH.getStaffId()}`
                     : AUTH.isDirector() ? CONFIG.access.directorCode
                     : AUTH.isCoDirector() ? CONFIG.access.coDirectorCode
                     : AUTH.getCode(),
      author_staff_id: staffCoach ? AUTH.getStaffId() : null,
      // Rubric format this evaluation was recorded against. Captures whether
      // the rater used field skills or GK skills - drives view / edit / PDF.
      eval_format:   _state.format || 'field_v1',
      nominated_strength: _state.nominatedStrength || null,
      nominated_working:  _state.nominatedWorking  || null,
      strong_points: _state.strongPoints  || null,
      working_points:_state.workingPoints || null,
      notes:         _state.sessionNotes  || null,
    };

    const rawRatings = ratedKeys.map(key => {
      const [domain, ...rest] = key.split('::');
      return {
        domain,
        skill:   rest.join('::'),
        level:   _state.ratings[key],
        comment: _state.comments[key] || null,
      };
    });

    // Deduplicate by domain::skill - keep last occurrence (belt-and-braces)
    const seen = {};
    for (const r of rawRatings) { seen[`${r.domain}::${r.skill}`] = r; }
    const ratingsPayload = Object.values(seen);

    try {
      if (_state.evalId) {
        // Update existing evaluation
        await DB.evaluations.update(_state.evalId, evalPayload, ratingsPayload);
        UI.toast('Updated', 'success');
      } else {
        // Create new evaluation
        await DB.evaluations.create(evalPayload, ratingsPayload);
        UI.toast('Saved', 'success');
      }

      // Clear draft on successful save
      DRAFT.clear(_state.player.id, _state.team.id, AUTH.getCode());

      // Return to player list after short delay
      setTimeout(() => APP.showPlayerList(), 800);

    } catch (err) {
      console.error('Save failed:', err);
      UI.toast('Save failed - check connection', 'error');
    } finally {
      _state.saving = false;
      _setSaveButtonState('idle');
    }
  }

  // ── DRAFT ────────────────────────────────────────────────────────────────────

  function clearDraft() {
    DRAFT.clear(_state.player.id, _state.team.id, AUTH.getCode());
    _resetRatings();
    _showDraftBanner(false);
    _render();
  }

  // ── HELPERS ──────────────────────────────────────────────────────────────────

  function _resetRatings() {
    _state.ratings      = {};
    _state.comments     = {};
    _state.strongPoints = '';
    _state.nominatedStrength = '';
    _state.nominatedWorking = '';
    _state.workingPoints= '';
    _state.sessionNotes = '';
    _state.referenceRatings = {};
  }

  // Pull team_score_ceilings rows for this team+season.
  async function _loadCeilings() {
    _state.ceilings = [];
    if (!_state.team?.id) return;
    // Director's UI is never capped - skip the fetch entirely so we don't
    // even paint the "Director's ceiling" hint or grey out any buttons.
    if (AUTH.isDirector()) return;
    try {
      _state.ceilings = await CALIBRATION.fetchCeilingsForTeam(
        _state.team.id,
        CONFIG.season.current
      );
    } catch { _state.ceilings = []; }
  }

  /** Effective ceiling for the current team at (domain, skill). 5 if unset. */
  function _ceilingFor(domain, skill) {
    return CALIBRATION.getEffectiveCeiling(_state.ceilings, domain, skill);
  }

  // ── REFERENCE OVERLAY (director-only) ────────────────────────────────────────

  /** Clear the reference overlay - used when the evaluated team changes. */
  function _resetReference() {
    _state.referenceCoach   = null;
    _state.referenceOn      = false;
    _state.referenceRatings = {};
    _state.teamEvals        = null;
  }

  /** Lazily fetch + cache every evaluation on the current team this season. */
  async function _ensureTeamEvals() {
    if (_state.teamEvals) return _state.teamEvals;
    try {
      _state.teamEvals = await DB.evaluations.getByTeam(
        _state.team.id, CONFIG.season.current
      );
    } catch {
      _state.teamEvals = [];
    }
    return _state.teamEvals;
  }

  // Toggle the reference overlay on/off.
  async function toggleReference() {
    if (!AUTH.isDirector()) return;

    if (_state.referenceOn) {
      _state.referenceOn = false;
      _renderSkillsArea();
      _renderReferenceToggle();
      return;
    }

    if (!_state.referenceCoach) {
      const picked = await _pickReferenceCoach();
      if (!picked) return;            // cancelled, or no coach evaluations
      _state.referenceCoach = picked;
    }
    _state.referenceOn = true;
    await _loadReferenceRatings();
    _renderSkillsArea();
    _renderReferenceToggle();

    if (!Object.keys(_state.referenceRatings).length) {
      UI.toast(`${_state.referenceCoach.coach_name} hasn't rated this player`, 'info');
    }
  }

  // Modal picker - which coach's evaluation to reference.
  async function _pickReferenceCoach() {
    const evals = await _ensureTeamEvals();
    const seen = {};
    (evals || []).forEach(ev => {
      if (!ev.team_code) return;      // skip director (code-less) evaluations
      if (!seen[ev.team_code]) seen[ev.team_code] = ev.coach_name || 'Coach';
    });
    const coaches = Object.entries(seen)
      .map(([team_code, coach_name]) => ({ team_code, coach_name }));

    if (!coaches.length) {
      UI.toast('No coach evaluations on this team yet', 'info');
      return null;
    }

    return new Promise(resolve => {
      const buttons = coaches.map(c => ({
        label:  c.coach_name,
        action: () => resolve(c),
      }));
      buttons.push({ label: 'Cancel', action: () => resolve(null) });
      UI.modal({
        title: 'Reference coach',
        body:  "Show this coach's ratings as a dot while you evaluate. You stay the assessor - it's only a reference.",
        buttons,
      });
    });
  }

  // Fetch the reference coach's ratings for the current player into _state.referenceRatings.
  async function _loadReferenceRatings() {
    _state.referenceRatings = {};
    if (!_state.referenceOn || !_state.referenceCoach || !_state.player) return;

    const evals = await _ensureTeamEvals();
    const rec = (evals || []).find(ev =>
      ev.player_id === _state.player.id &&
      ev.team_code === _state.referenceCoach.team_code
    );
    if (!rec) return;   // this coach hasn't evaluated this player

    try {
      const { ratings } = await DB.evaluations.getById(rec.id);
      (ratings || []).forEach(r => {
        _state.referenceRatings[`${r.domain}::${r.skill}`] = r.level;
      });
    } catch {
      _state.referenceRatings = {};
    }
  }

  /** Show + style the discreet eye toggle - only ever visible to the director. */
  function _renderReferenceToggle() {
    const btn = document.getElementById('rate-reference-toggle');
    if (!btn) return;
    if (!AUTH.isDirector()) {
      btn.style.display = 'none';
      return;
    }
    btn.style.display = '';
    btn.classList.toggle('rate-ref-toggle--active', _state.referenceOn);
    const coach = _state.referenceCoach ? ` (${_state.referenceCoach.coach_name})` : '';
    btn.title = _state.referenceOn
      ? `Coach reference shown${coach} - tap to hide`
      : "Show a coach's ratings as a reference";
  }

  function _hasUnsavedData() {
    const hasRatings = Object.values(_state.ratings).some(v => v > 0);
    return hasRatings || _state.strongPoints || _state.workingPoints || _state.sessionNotes;
  }

  function _autoSaveDraft() {
    if (!_state.player) return;
    DRAFT.save(_state.player.id, _state.team.id, AUTH.getCode(), {
      ratings:      _state.ratings,
      comments:     _state.comments,
      strongPoints: _state.strongPoints,
      nominatedStrength: _state.nominatedStrength,
      nominatedWorking:  _state.nominatedWorking,
      workingPoints:_state.workingPoints,
      sessionNotes: _state.sessionNotes,
    });
  }

  function _syncFeedbackInputs() {
    // Drop any in-progress dictation when leaving / submitting the feedback tab.
    if (typeof DICTATION !== 'undefined') DICTATION.cancelActive();
    const strong  = document.getElementById('feedback-strong');
    const working = document.getElementById('feedback-working');
    const notes   = document.getElementById('feedback-notes');
    if (strong)  _state.strongPoints  = strong.value;
    if (working) _state.workingPoints = working.value;
    if (notes)   _state.sessionNotes  = notes.value;
  }

  function _goToPlayer(index) {
    const player = _state.playerList[index];
    if (!player) return;
    startNew(player, _state.team, _state.playerList, index);
  }

  function _showDraftBanner(show) {
    const el = document.getElementById('draft-banner');
    if (el) el.style.display = show ? 'flex' : 'none';
  }

  function _setSaveButtonState(state) {
    const btn = document.getElementById('save-btn');
    if (!btn) return;
    if (state === 'saving') {
      btn.disabled = true;
      btn.textContent = 'Saving...';
    } else {
      btn.disabled = false;
      btn.textContent = _state.evalId ? 'Update evaluation' : 'Save evaluation';
    }
  }

  // ── RENDER ───────────────────────────────────────────────────────────────────

  function _render() {
    _renderHeader();
    _renderDomainTabs();
    _renderSkillsArea();
    _updateProgress();
    _setSaveButtonState('idle');
    _renderReferenceToggle();
  }

  function _renderHeader() {
    const nameEl = document.getElementById('rate-player-name');
    const subEl  = document.getElementById('rate-player-sub');
    const posEl  = document.getElementById('player-position');
    const prevBtn= document.getElementById('btn-prev-player');
    const nextBtn= document.getElementById('btn-next-player');

    if (nameEl) nameEl.textContent = _state.player?.name || '';
    if (subEl) {
      const parts = [
        _state.team?.id,
        AUTH.getCoachName(),
        _state.format === 'gk_v1' ? '· GK rubric' : null,
        _state.evalId ? '· Editing' : null,
        AUTH.isFeedback() ? '· Feedback only' : null,
      ].filter(Boolean);
      subEl.textContent = parts.join(' · ');
    }
    if (posEl) {
      posEl.textContent = `${_state.playerIndex + 1} / ${_state.playerList.length}`;
    }
    if (prevBtn) prevBtn.disabled = _state.playerIndex === 0;
    if (nextBtn) nextBtn.disabled = _state.playerIndex === _state.playerList.length - 1;
  }

  function _renderDomainTabs() {
    const container = document.getElementById('domain-tabs');
    if (!container) return;

    const allDomains     = getDomains();
    const visibleDomains = AUTH.isFeedback()
      ? []
      : allDomains.filter(d => AUTH.canSeePillar(d));

    // Which domains have any ratings
    const ratedDomains = {};
    visibleDomains.forEach(d => {
      ratedDomains[d] = getSkillsFor(d, _state.format).some(sk => _state.ratings[`${d}::${sk}`] > 0);
    });

    container.innerHTML = UI.domainTabs(
      visibleDomains,
      _state.domain,
      ratedDomains,
      true  // always show Feedback tab
    );

    // Attach click handlers
    container.querySelectorAll('[data-domain]').forEach(tab => {
      tab.addEventListener('click', () => setDomain(tab.dataset.domain));
    });
  }

  /**
   * Skill names rated in this evaluation (level > 0), in rubric order. The
   * nomination pickers offer only these: naming a skill you did not assess
   * would be a judgment with nothing behind it.
   */
  function _ratedSkillNames() {
    return Object.entries(_state.ratings || {})
      .filter(([, lvl]) => lvl > 0)
      .map(([key]) => key.split('::')[1])
      .filter(Boolean);
  }

  function _renderSkillsArea() {
    const area = document.getElementById('skills-area');
    if (!area) return;

    if (_state.domain === 'Feedback') {
      area.innerHTML = UI.feedbackPillar({
        strongPoints:  _state.strongPoints,
        workingPoints: _state.workingPoints,
        sessionNotes:  _state.sessionNotes,
        // Only what was actually rated here can be nominated.
        ratedSkills:   _ratedSkillNames(),
        nominatedStrength: _state.nominatedStrength,
        nominatedWorking:  _state.nominatedWorking,
      });

      const nomS = document.getElementById('nominate-strength');
      const nomW = document.getElementById('nominate-working');
      if (nomS) nomS.addEventListener('change', () => setNominatedStrength(nomS.value));
      if (nomW) nomW.addEventListener('change', () => setNominatedWorking(nomW.value));

      // Attach live-save handlers
      const strong  = document.getElementById('feedback-strong');
      const working = document.getElementById('feedback-working');
      const notes   = document.getElementById('feedback-notes');
      if (strong)  strong.addEventListener('input',  () => setStrongPoints(strong.value));
      if (working) working.addEventListener('input', () => setWorkingPoints(working.value));
      if (notes)   notes.addEventListener('input',   () => setSessionNotes(notes.value));

      // Voice dictation - the shared module appends transcribed text and fires
      // an `input` event, so the live-save handlers above pick it up. onOrphan
      // covers the rare case of switching tabs mid-transcription.
      if (typeof DICTATION !== 'undefined') {
        if (strong)  DICTATION.attach(strong,  {
          onOrphan: t => setStrongPoints(((_state.strongPoints || '') + ' ' + t).trim()),
        });
        if (working) DICTATION.attach(working, {
          onOrphan: t => setWorkingPoints(((_state.workingPoints || '') + ' ' + t).trim()),
        });
        if (notes)   DICTATION.attach(notes,   {
          onOrphan: t => setSessionNotes(((_state.sessionNotes || '') + ' ' + t).trim()),
        });
      }
      return;
    }

    const skills = getSkillsFor(_state.domain, _state.format);
    const ageGroup = pillarBandForTeam(_state.team?.id, _state.team?.age_group);

    area.innerHTML = skills.map(skill => {
      const key = `${_state.domain}::${skill}`;
      return UI.skillCard({
        domain:        _state.domain,
        skill,
        ageGroup,
        selectedLevel: _state.ratings[key] || 0,
        comment:       _state.comments[key] || '',
        maxLevel:      _ceilingFor(_state.domain, skill),
        referenceLevel: (AUTH.isDirector() && _state.referenceOn)
          ? (_state.referenceRatings[key] || 0)
          : 0,
      });
    }).join('');

    // Attach level button handlers
    area.querySelectorAll('[data-level]').forEach(btn => {
      btn.addEventListener('click', () => {
        setRating(btn.dataset.domain, btn.dataset.skill, parseInt(btn.dataset.level));
      });
    });

    // Attach comment toggle handlers
    area.querySelectorAll('[data-skill-id]').forEach(toggle => {
      toggle.addEventListener('click', () => {
        const skillId = toggle.dataset.skillId;
        const box     = document.getElementById(`comment-box-${skillId}`);
        if (!box) return;
        const isOpen = box.classList.toggle('skill-card__comment-box--open');
        toggle.classList.toggle('skill-card__comment-toggle--open', isOpen);
        toggle.textContent = isOpen ? 'hide comment' : '+ add comment';
      });
    });

    // Attach comment input handlers
    area.querySelectorAll('[data-skill]').forEach(input => {
      if (input.tagName === 'TEXTAREA') {
        input.addEventListener('input', () => {
          setComment(input.dataset.domain, input.dataset.skill, input.value);
        });
      }
    });
  }

  function _updateProgress() {
    const total = getTotalCriteria(_state.format);
    const rated = Object.values(_state.ratings).filter(v => v > 0).length;
    UI.updateProgress('progress-info', rated, total);
  }

  return {
    startNew,
    startEdit,
    prevPlayer,
    nextPlayer,
    goBack,
    submit,
    clearDraft,
    toggleReference,
  };

})();
